import type { CompanyQuarter, CompanySeries, DataPanel, SourceInfo } from "../types";

const CORE_COMPANY_IDS = ["alphabet", "microsoft", "amazon", "meta"] as const;
const OPTIONAL_COMPANY_IDS = ["oracle"] as const;
const MIN_TRAIN_ROWS = 16;
const MIN_EXPERT_HISTORY = 4;
const MIN_CONFORMAL_RESIDUALS = 9;
const RECENT_WINDOW = 8;
const INTERVAL_LEVEL = 0.9;
const RIDGE_LAMBDA = 1.25;

export type CompanyCapexExpert = "lag1" | "drift" | "ridge";
export type CompanyCapexIntervalMethod = "oos-conformal" | "rmse-fallback" | "component-rss";

export interface CompanyCapexScenario {
  /** Multiples future forecasts only. Historical walk-forward points stay fixed. */
  capexIntensity?: number;
}

export interface CompanyCapexMonitorRow {
  id: string;
  name: string;
  period: string;
  capex: number;
  revenue: number;
  filingDate: string;
  ageDays: number;
}

export interface CompanyCapexMonitor {
  commonPeriod: string;
  asOf: string;
  latestFilingDate: string;
  companies: CompanyCapexMonitorRow[];
  totalCapex: number;
  source: SourceInfo & { vintagePolicy: string };
}

export interface CompanyCapexExpertMetrics {
  expert: CompanyCapexExpert | "selected" | "bottom-up";
  observations: number;
  rmse: number;
  recentRmse: number;
  mape: number;
  mae: number;
}

export interface CompanyCapexBacktestPoint {
  period: string;
  actual: number;
  predicted: number;
  priorActual: number;
  low: number;
  high: number;
  intervalMethod: CompanyCapexIntervalMethod;
  intervalCalibrated: boolean;
  selectedExpert: CompanyCapexExpert | "bottom-up";
  predictions: Record<CompanyCapexExpert, number>;
}

export interface CompanyCapexBacktestSeries {
  id: string;
  name: string;
  points: CompanyCapexBacktestPoint[];
  metrics: CompanyCapexExpertMetrics[];
}

export interface CompanyCapexForecastPoint {
  horizon: 1 | 2 | 3 | 4;
  period: string;
  predicted: number;
  low: number;
  high: number;
  intervalMethod: CompanyCapexIntervalMethod;
  intervalCalibrated: boolean;
  residualCount: number;
}

export interface CompanyCapexForecastPath {
  id: string;
  name: string;
  latestActual: number;
  selectedExpert: CompanyCapexExpert | "bottom-up";
  points: CompanyCapexForecastPoint[];
  selection: {
    promotedBeyondLag1: boolean;
    rule: string;
    fullWindowRmse: number;
    recentRmse: number;
    lag1FullWindowRmse: number;
    lag1RecentRmse: number;
    multiHorizonRmse?: number;
    recentMultiHorizonRmse?: number;
    lag1MultiHorizonRmse?: number;
    lag1RecentMultiHorizonRmse?: number;
  };
}

export interface CompanyCapexSliceResult {
  monitor: CompanyCapexMonitor;
  forecast: {
    commonPeriod: string;
    companies: CompanyCapexForecastPath[];
    total: CompanyCapexForecastPath;
  };
  backtest: {
    companies: CompanyCapexBacktestSeries[];
    total: CompanyCapexBacktestSeries;
  };
  methodology: {
    target: string;
    features: string[];
    validation: string;
    selection: string;
    interval: string;
    multiStep: string;
    scenario: string;
    vintagePolicy: string;
  };
  sources: SourceInfo[];
}

interface ModelRow {
  period: string;
  capex: number;
  revenue: number;
}

interface RidgeFit {
  predict: (features: number[]) => number;
}

interface CompanyModelOutput {
  forecast: CompanyCapexForecastPath;
  backtest: CompanyCapexBacktestSeries;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function completeFirstReported(row: CompanyQuarter): row is CompanyQuarter & {
  capexAsFirstReported: number;
  revenueFirstReported: number;
} {
  return isFiniteNumber(row.capexAsFirstReported) && isFiniteNumber(row.revenueFirstReported);
}

function nextPeriod(period: string) {
  const match = /^(\d{4})-Q([1-4])$/.exec(period);
  if (!match) throw new Error(`Invalid quarter: ${period}`);
  const year = Number(match[1]);
  const quarter = Number(match[2]);
  return quarter === 4 ? `${year + 1}-Q1` : `${year}-Q${quarter + 1}`;
}

function selectedCompanies(panel: DataPanel) {
  const required = CORE_COMPANY_IDS.map((id) => {
    const company = panel.companies[id];
    if (!company) throw new Error(`Missing required company series: ${id}`);
    return company;
  });
  const optional = OPTIONAL_COMPANY_IDS.flatMap((id) => panel.companies[id] ? [panel.companies[id]] : []);
  return [...required, ...optional];
}

function latestCommonPeriod(companies: CompanySeries[]) {
  const common = companies
    .map((company) => new Set(company.quarters.filter(completeFirstReported).map((row) => row.period)))
    .reduce((left, right) => new Set([...left].filter((period) => right.has(period))));
  const latest = [...common].sort().at(-1);
  if (!latest) throw new Error("No common SEC first-reported quarter across selected companies.");
  return latest;
}

function filingDate(row: CompanyQuarter) {
  const capex = row.capexFiledFirst ?? row.firstFiled ?? row.filed;
  const revenue = row.revenueFiledFirst ?? row.firstFiled ?? row.filed;
  if (!capex) throw new Error(`${row.period}: first-reported CapEx filing date is missing.`);
  if (!revenue) throw new Error(`${row.period}: first-reported revenue filing date is missing.`);
  return [capex, revenue].sort().at(-1)!;
}

function daysBetween(start: string, end: string) {
  const value = (Date.parse(end) - Date.parse(start)) / 86_400_000;
  if (!Number.isFinite(value)) throw new Error(`Invalid date range: ${start} → ${end}`);
  if (value < 0) throw new Error(`First-reported filing ${start} is after panel asOf ${end}.`);
  return Math.floor(value);
}

function rowsFor(company: CompanySeries, commonPeriod: string): ModelRow[] {
  const rows = [...company.quarters]
    .filter((row) => row.period <= commonPeriod)
    .sort((left, right) => left.period.localeCompare(right.period));
  if (rows.length < MIN_TRAIN_ROWS + 1) {
    throw new Error(`${company.id}: ${rows.length} complete quarters; at least ${MIN_TRAIN_ROWS + 1} are required.`);
  }
  return rows.map((row, index): ModelRow => {
    if (!completeFirstReported(row)) {
      throw new Error(`${company.id} ${row.period}: SEC first-reported CapEx and revenue are required.`);
    }
    if (index > 0 && row.period !== nextPeriod(rows[index - 1].period)) {
      throw new Error(`${company.id}: non-contiguous quarter ${rows[index - 1].period} → ${row.period}.`);
    }
    return { period: row.period, capex: row.capexAsFirstReported, revenue: row.revenueFirstReported };
  });
}

function solveLinear(matrix: number[][], vector: number[]) {
  const augmented = matrix.map((row, index) => [...row, vector[index]]);
  for (let column = 0; column < augmented.length; column += 1) {
    let pivot = column;
    for (let row = column + 1; row < augmented.length; row += 1) {
      if (Math.abs(augmented[row][column]) > Math.abs(augmented[pivot][column])) pivot = row;
    }
    [augmented[column], augmented[pivot]] = [augmented[pivot], augmented[column]];
    const divisor = Math.abs(augmented[column][column]) < 1e-10 ? 1e-10 : augmented[column][column];
    for (let item = column; item <= augmented.length; item += 1) augmented[column][item] /= divisor;
    for (let row = 0; row < augmented.length; row += 1) {
      if (row === column) continue;
      const factor = augmented[row][column];
      for (let item = column; item <= augmented.length; item += 1) {
        augmented[row][item] -= factor * augmented[column][item];
      }
    }
  }
  return augmented.map((row) => row[augmented.length]);
}

function fitRidge(samples: number[][], targets: number[]): RidgeFit {
  if (!samples.length || samples.length !== targets.length) throw new Error("Ridge requires aligned training samples.");
  const columns = samples[0].length;
  const means = Array.from({ length: columns }, (_, column) =>
    samples.reduce((sum, row) => sum + row[column], 0) / samples.length);
  const scales = means.map((_, column) => {
    const variance = samples.reduce((sum, row) => sum + (row[column] - means[column]) ** 2, 0)
      / Math.max(1, samples.length - 1);
    return Math.sqrt(variance) || 1;
  });
  const normalized = samples.map((row) => [1, ...row.map((value, column) => (value - means[column]) / scales[column])]);
  const width = columns + 1;
  const matrix = Array.from({ length: width }, () => Array(width).fill(0));
  const vector = Array(width).fill(0);
  normalized.forEach((row, sampleIndex) => {
    row.forEach((left, leftIndex) => {
      vector[leftIndex] += left * targets[sampleIndex];
      row.forEach((right, rightIndex) => { matrix[leftIndex][rightIndex] += left * right; });
    });
  });
  for (let index = 1; index < width; index += 1) matrix[index][index] += RIDGE_LAMBDA;
  const coefficients = solveLinear(matrix, vector);
  return {
    predict: (features) => coefficients[0] + features.reduce(
      (sum, value, column) => sum + coefficients[column + 1] * ((value - means[column]) / scales[column]),
      0,
    ),
  };
}

function featuresForTarget(rows: ModelRow[], targetIndex: number) {
  if (targetIndex < 4) throw new Error("Four prior quarters are required for company CapEx features.");
  const capexHistory = rows.slice(targetIndex - 4, targetIndex).map((row) => row.capex);
  return [
    rows[targetIndex - 1].capex,
    rows[targetIndex - 4].capex,
    rows[targetIndex - 1].revenue,
    capexHistory.reduce((sum, value) => sum + value, 0) / 4,
    targetIndex,
  ];
}

function ridgeAt(rows: ModelRow[], targetIndex: number) {
  const samples: number[][] = [];
  const targets: number[] = [];
  for (let index = 4; index < targetIndex; index += 1) {
    samples.push(featuresForTarget(rows, index));
    targets.push(rows[index].capex);
  }
  return fitRidge(samples, targets).predict(featuresForTarget(rows, targetIndex));
}

function expertPredictions(rows: ModelRow[], targetIndex: number): Record<CompanyCapexExpert, number> {
  const lag1 = rows[targetIndex - 1].capex;
  const drift = lag1 + (lag1 - rows[targetIndex - 5].capex) / 4;
  return { lag1, drift: Math.max(0, drift), ridge: Math.max(0, ridgeAt(rows, targetIndex)) };
}

function rmse(errors: number[]) {
  return Math.sqrt(errors.reduce((sum, value) => sum + value ** 2, 0) / Math.max(1, errors.length));
}

function expertScore(errors: number[]) {
  const recent = errors.slice(-Math.min(RECENT_WINDOW, errors.length));
  return (rmse(errors) + rmse(recent)) / 2;
}

/** Only experts no worse than lag-1 in both full and recent windows may be promoted. */
function selectExpert(errors: Record<CompanyCapexExpert, number[]>): CompanyCapexExpert {
  const lag1 = errors.lag1;
  if (lag1.length < MIN_EXPERT_HISTORY) return "lag1";
  const lagFull = rmse(lag1);
  const lagRecent = rmse(lag1.slice(-Math.min(RECENT_WINDOW, lag1.length)));
  const eligible = (["drift", "ridge"] as const).filter((expert) => {
    if (errors[expert].length !== lag1.length) return false;
    const full = rmse(errors[expert]);
    const recent = rmse(errors[expert].slice(-Math.min(RECENT_WINDOW, errors[expert].length)));
    return full <= lagFull + 1e-12 && recent <= lagRecent + 1e-12;
  });
  return (["lag1", ...eligible] as CompanyCapexExpert[])
    .sort((left, right) => expertScore(errors[left]) - expertScore(errors[right]))[0];
}

function conformalRadius(errors: number[]) {
  const values = errors.map(Math.abs).sort((left, right) => left - right);
  const rank = Math.ceil((values.length + 1) * INTERVAL_LEVEL);
  if (values.length < MIN_CONFORMAL_RESIDUALS || rank > values.length) {
    throw new Error(`90% conformal requires at least ${MIN_CONFORMAL_RESIDUALS} prior OOS residuals.`);
  }
  return values[rank - 1];
}

function fallbackRadius(errors: number[], center: number) {
  if (!errors.length) return Math.max(0.25 * Math.abs(center), 0.1);
  return 1.645 * Math.sqrt(1 + 1 / errors.length) * rmse(errors);
}

function metricsFor(
  expert: CompanyCapexExpertMetrics["expert"],
  actuals: number[],
  predictions: number[],
): CompanyCapexExpertMetrics {
  const errors = predictions.map((value, index) => value - actuals[index]);
  const recent = errors.slice(-Math.min(RECENT_WINDOW, errors.length));
  return {
    expert,
    observations: errors.length,
    rmse: rmse(errors),
    recentRmse: rmse(recent),
    mape: errors.reduce((sum, error, index) => sum + Math.abs(error / Math.max(Math.abs(actuals[index]), 1e-9)), 0)
      / Math.max(1, errors.length) * 100,
    mae: errors.reduce((sum, error) => sum + Math.abs(error), 0) / Math.max(1, errors.length),
  };
}

function companyBacktest(company: CompanySeries, rows: ModelRow[]): CompanyCapexBacktestSeries {
  const errors: Record<CompanyCapexExpert, number[]> = { lag1: [], drift: [], ridge: [] };
  const selectedErrors: number[] = [];
  const points: CompanyCapexBacktestPoint[] = [];
  for (let targetIndex = MIN_TRAIN_ROWS; targetIndex < rows.length; targetIndex += 1) {
    const predictions = expertPredictions(rows, targetIndex);
    const selectedExpert = selectExpert(errors);
    const predicted = predictions[selectedExpert];
    const calibrated = selectedErrors.length >= MIN_CONFORMAL_RESIDUALS;
    const radius = calibrated
      ? conformalRadius(selectedErrors)
      : fallbackRadius(selectedErrors, predicted);
    const actual = rows[targetIndex].capex;
    points.push({
      period: rows[targetIndex].period,
      actual,
      predicted,
      priorActual: rows[targetIndex - 1].capex,
      low: Math.max(0, predicted - radius),
      high: predicted + radius,
      intervalMethod: calibrated ? "oos-conformal" : "rmse-fallback",
      intervalCalibrated: calibrated,
      selectedExpert,
      predictions,
    });
    (["lag1", "drift", "ridge"] as const).forEach((expert) => {
      errors[expert].push(predictions[expert] - actual);
    });
    selectedErrors.push(predicted - actual);
  }
  if (!points.length) throw new Error(`${company.id}: no rolling-origin backtest points were produced.`);
  const actuals = points.map((point) => point.actual);
  return {
    id: company.id,
    name: company.name,
    points,
    metrics: [
      ...(["lag1", "drift", "ridge"] as const).map((expert) =>
        metricsFor(expert, actuals, points.map((point) => point.predictions[expert]))),
      metricsFor("selected", actuals, points.map((point) => point.predicted)),
    ],
  };
}

function median(values: number[]) {
  const ordered = [...values].sort((left, right) => left - right);
  const middle = Math.floor(ordered.length / 2);
  return ordered.length % 2 ? ordered[middle] : (ordered[middle - 1] + ordered[middle]) / 2;
}

function recursivePath(rows: ModelRow[], expert: CompanyCapexExpert) {
  const extended = rows.map((row) => ({ ...row }));
  const finalFit = (() => {
    const samples: number[][] = [];
    const targets: number[] = [];
    for (let index = 4; index < rows.length; index += 1) {
      samples.push(featuresForTarget(rows, index));
      targets.push(rows[index].capex);
    }
    return fitRidge(samples, targets);
  })();
  const revenueGrowth = Math.min(1.5, Math.max(0.8, median(
    rows.slice(-4).map((row, offset) => row.revenue / rows[rows.length - 8 + offset].revenue),
  )));
  const path: Array<{ period: string; predicted: number }> = [];
  for (let horizon = 1; horizon <= 4; horizon += 1) {
    const targetIndex = extended.length;
    const lag1 = extended[targetIndex - 1].capex;
    const drift = lag1 + (lag1 - extended[targetIndex - 5].capex) / 4;
    const ridge = finalFit.predict(featuresForTarget(extended, targetIndex));
    const predicted = Math.max(0, { lag1, drift, ridge }[expert]);
    const period = nextPeriod(extended[targetIndex - 1].period);
    const revenue = extended[targetIndex - 4].revenue * revenueGrowth;
    extended.push({ period, capex: predicted, revenue });
    path.push({ period, predicted });
  }
  return path;
}

function multiHorizonErrors(rows: ModelRow[], expert: CompanyCapexExpert) {
  const errors: number[] = [];
  for (let origin = MIN_TRAIN_ROWS; origin < rows.length; origin += 1) {
    const availableHorizons = Math.min(4, rows.length - origin);
    const path = recursivePath(rows.slice(0, origin), expert);
    for (let index = 0; index < availableHorizons; index += 1) {
      errors.push(path[index].predicted - rows[origin + index].capex);
    }
  }
  if (!errors.length) throw new Error(`${expert}: no company CapEx multi-horizon errors.`);
  return errors;
}

function selectForecastExpert(
  oneStepErrors: Record<CompanyCapexExpert, number[]>,
  horizonErrors: Record<CompanyCapexExpert, number[]>,
) {
  const lagRecent = rmse(oneStepErrors.lag1.slice(-Math.min(RECENT_WINDOW, oneStepErrors.lag1.length)));
  const lagMulti = rmse(horizonErrors.lag1);
  const lagMultiRecent = rmse(horizonErrors.lag1.slice(-Math.min(RECENT_WINDOW, horizonErrors.lag1.length)));
  const eligible = (["drift", "ridge"] as const).filter((expert) =>
    rmse(oneStepErrors[expert].slice(-Math.min(RECENT_WINDOW, oneStepErrors[expert].length))) <= lagRecent + 1e-12
    && rmse(horizonErrors[expert]) <= lagMulti + 1e-12
    && rmse(horizonErrors[expert].slice(-Math.min(RECENT_WINDOW, horizonErrors[expert].length))) <= lagMultiRecent + 1e-12);
  const robustScore = (expert: CompanyCapexExpert) => (
    rmse(oneStepErrors[expert])
    + rmse(oneStepErrors[expert].slice(-Math.min(RECENT_WINDOW, oneStepErrors[expert].length)))
    + rmse(horizonErrors[expert])
    + rmse(horizonErrors[expert].slice(-Math.min(RECENT_WINDOW, horizonErrors[expert].length)))
  ) / 4;
  return (["lag1", ...eligible] as CompanyCapexExpert[])
    .sort((left, right) => robustScore(left) - robustScore(right))[0];
}

function companyModel(
  company: CompanySeries,
  commonPeriod: string,
  intensity: number,
): CompanyModelOutput {
  const rows = rowsFor(company, commonPeriod);
  const backtest = companyBacktest(company, rows);
  const errorHistory: Record<CompanyCapexExpert, number[]> = {
    lag1: backtest.points.map((point) => point.predictions.lag1 - point.actual),
    drift: backtest.points.map((point) => point.predictions.drift - point.actual),
    ridge: backtest.points.map((point) => point.predictions.ridge - point.actual),
  };
  const horizonErrorHistory: Record<CompanyCapexExpert, number[]> = {
    lag1: multiHorizonErrors(rows, "lag1"),
    drift: multiHorizonErrors(rows, "drift"),
    ridge: multiHorizonErrors(rows, "ridge"),
  };
  const selectedExpert = selectForecastExpert(errorHistory, horizonErrorHistory);
  const selectedMetric = backtest.metrics.find((metric) => metric.expert === selectedExpert)!;
  const lag1Metric = backtest.metrics.find((metric) => metric.expert === "lag1")!;
  const selectedErrors = backtest.points.map((point) => point.predicted - point.actual);
  const calibrated = selectedErrors.length >= MIN_CONFORMAL_RESIDUALS;
  const baseRadius = calibrated
    ? conformalRadius(selectedErrors)
    : fallbackRadius(selectedErrors, rows.at(-1)!.capex);
  const points = recursivePath(rows, selectedExpert).map((point, index): CompanyCapexForecastPoint => {
    const horizon = (index + 1) as 1 | 2 | 3 | 4;
    const predicted = point.predicted * intensity;
    const radius = baseRadius * intensity * Math.sqrt(horizon);
    return {
      horizon,
      period: point.period,
      predicted,
      low: Math.max(0, predicted - radius),
      high: predicted + radius,
      intervalMethod: calibrated ? "oos-conformal" : "rmse-fallback",
      intervalCalibrated: calibrated,
      residualCount: selectedErrors.length,
    };
  });
  return {
    backtest,
    forecast: {
      id: company.id,
      name: company.name,
      latestActual: rows.at(-1)!.capex,
      selectedExpert,
      points,
      selection: {
        promotedBeyondLag1: selectedExpert !== "lag1",
        rule: "A 4Q expert must beat lag-1 on recent one-step and both full/recent direct 1Q-4Q RMSE; historical walk-forward selection remains one-step point-in-time.",
        fullWindowRmse: selectedMetric.rmse,
        recentRmse: selectedMetric.recentRmse,
        lag1FullWindowRmse: lag1Metric.rmse,
        lag1RecentRmse: lag1Metric.recentRmse,
        multiHorizonRmse: rmse(horizonErrorHistory[selectedExpert]),
        recentMultiHorizonRmse: rmse(horizonErrorHistory[selectedExpert].slice(-Math.min(RECENT_WINDOW, horizonErrorHistory[selectedExpert].length))),
        lag1MultiHorizonRmse: rmse(horizonErrorHistory.lag1),
        lag1RecentMultiHorizonRmse: rmse(horizonErrorHistory.lag1.slice(-Math.min(RECENT_WINDOW, horizonErrorHistory.lag1.length))),
      },
    },
  };
}

function aggregateBacktest(companySeries: CompanyCapexBacktestSeries[]): CompanyCapexBacktestSeries {
  const maps = companySeries.map((series) => new Map(series.points.map((point) => [point.period, point])));
  const periods = companySeries[0].points
    .map((point) => point.period)
    .filter((period) => maps.every((map) => map.has(period)));
  const priorErrors: number[] = [];
  const points = periods.map((period): CompanyCapexBacktestPoint => {
    const components = maps.map((map) => map.get(period)!);
    const actual = components.reduce((sum, point) => sum + point.actual, 0);
    const predicted = components.reduce((sum, point) => sum + point.predicted, 0);
    const calibrated = priorErrors.length >= MIN_CONFORMAL_RESIDUALS;
    const radius = calibrated
      ? conformalRadius(priorErrors)
      : Math.sqrt(components.reduce((sum, point) => sum + Math.max(point.predicted - point.low, point.high - point.predicted) ** 2, 0));
    const predictions = {
      lag1: components.reduce((sum, point) => sum + point.predictions.lag1, 0),
      drift: components.reduce((sum, point) => sum + point.predictions.drift, 0),
      ridge: components.reduce((sum, point) => sum + point.predictions.ridge, 0),
    };
    const output: CompanyCapexBacktestPoint = {
      period,
      actual,
      predicted,
      priorActual: components.reduce((sum, point) => sum + point.priorActual, 0),
      low: Math.max(0, predicted - radius),
      high: predicted + radius,
      intervalMethod: calibrated ? "oos-conformal" : "component-rss",
      intervalCalibrated: calibrated,
      selectedExpert: "bottom-up",
      predictions,
    };
    priorErrors.push(predicted - actual);
    return output;
  });
  const actuals = points.map((point) => point.actual);
  return {
    id: "total",
    name: "Hyperscaler CapEx total",
    points,
    metrics: [
      ...(["lag1", "drift", "ridge"] as const).map((expert) =>
        metricsFor(expert, actuals, points.map((point) => point.predictions[expert]))),
      metricsFor("bottom-up", actuals, points.map((point) => point.predicted)),
    ],
  };
}

function aggregateForecast(
  companies: CompanyCapexForecastPath[],
  backtest: CompanyCapexBacktestSeries,
  intensity: number,
): CompanyCapexForecastPath {
  const errors = backtest.points.map((point) => point.predicted - point.actual);
  const calibrated = errors.length >= MIN_CONFORMAL_RESIDUALS;
  const baseRadius = calibrated
    ? conformalRadius(errors)
    : fallbackRadius(errors, companies.reduce((sum, company) => sum + company.latestActual, 0));
  const selectedMetric = backtest.metrics.find((metric) => metric.expert === "bottom-up")!;
  const lag1Metric = backtest.metrics.find((metric) => metric.expert === "lag1")!;
  return {
    id: "total",
    name: "Hyperscaler CapEx total",
    latestActual: companies.reduce((sum, company) => sum + company.latestActual, 0),
    selectedExpert: "bottom-up",
    points: ([0, 1, 2, 3] as const).map((index): CompanyCapexForecastPoint => {
      const predicted = companies.reduce((sum, company) => sum + company.points[index].predicted, 0);
      const horizon = (index + 1) as 1 | 2 | 3 | 4;
      const radius = baseRadius * intensity * Math.sqrt(horizon);
      return {
        horizon,
        period: companies[0].points[index].period,
        predicted,
        low: Math.max(0, predicted - radius),
        high: predicted + radius,
        intervalMethod: calibrated ? "oos-conformal" : "rmse-fallback",
        intervalCalibrated: calibrated,
        residualCount: errors.length,
      };
    }),
    selection: {
      promotedBeyondLag1: selectedMetric.rmse <= lag1Metric.rmse && selectedMetric.recentRmse <= lag1Metric.recentRmse,
      rule: "Bottom-up sum of each company's historically gated expert; aggregate interval uses aggregate OOS errors.",
      fullWindowRmse: selectedMetric.rmse,
      recentRmse: selectedMetric.recentRmse,
      lag1FullWindowRmse: lag1Metric.rmse,
      lag1RecentRmse: lag1Metric.recentRmse,
    },
  };
}

/**
 * Deep module for the company-level CapEx product slice. It is deterministic,
 * side-effect free, and accepts an already-vintaged panel from the ingestion seam.
 */
export function runCompanyCapexSlice(
  panel: DataPanel,
  scenario: CompanyCapexScenario = {},
): CompanyCapexSliceResult {
  const intensity = scenario.capexIntensity ?? 1;
  if (!isFiniteNumber(intensity) || intensity < 0.25 || intensity > 2.5) {
    throw new Error("capexIntensity must be a finite multiplier between 0.25 and 2.5.");
  }
  const companies = selectedCompanies(panel);
  const commonPeriod = latestCommonPeriod(companies);
  const monitorCompanies = companies.map((company): CompanyCapexMonitorRow => {
    const row = company.quarters.find((candidate) => candidate.period === commonPeriod);
    if (!row || !completeFirstReported(row)) {
      throw new Error(`${company.id}: ${commonPeriod} is missing SEC first-reported CapEx or revenue.`);
    }
    const filed = filingDate(row);
    return {
      id: company.id,
      name: company.name,
      period: commonPeriod,
      capex: row.capexAsFirstReported,
      revenue: row.revenueFirstReported,
      filingDate: filed,
      ageDays: daysBetween(filed, panel.meta.asOf),
    };
  });
  const latestFilingDate = monitorCompanies.map((company) => company.filingDate).sort().at(-1)!;
  const secSource = panel.sources.find((source) => source.id === "sec");
  if (!secSource) throw new Error("SEC source lineage is missing from the panel.");
  const outputs = companies.map((company) => companyModel(company, commonPeriod, intensity));
  const companyBacktests = outputs.map((output) => output.backtest);
  const totalBacktest = aggregateBacktest(companyBacktests);
  const companyForecasts = outputs.map((output) => output.forecast);
  const totalForecast = aggregateForecast(companyForecasts, totalBacktest, intensity);

  return {
    monitor: {
      commonPeriod,
      asOf: panel.meta.asOf,
      latestFilingDate,
      companies: monitorCompanies,
      totalCapex: monitorCompanies.reduce((sum, company) => sum + company.capex, 0),
      source: {
        ...secSource,
        vintagePolicy: "SEC first-reported CapEx and revenue only; latest-restated values are validation evidence only.",
      },
    },
    forecast: { commonPeriod, companies: companyForecasts, total: totalForecast },
    backtest: { companies: companyBacktests, total: totalBacktest },
    methodology: {
      target: "Company quarterly cash CapEx in USD billions.",
      features: [
        "SEC first-reported CapEx t-1",
        "SEC first-reported CapEx t-4",
        "SEC first-reported revenue t-1",
        "trailing-four-quarter first-reported CapEx mean",
        "time trend",
      ],
      validation: `Expanding-window rolling-origin backtest from observation ${MIN_TRAIN_ROWS + 1}; lag-1, four-quarter drift and ridge share identical forecast dates.`,
      selection: "The historical adaptive selector uses prior one-step errors only. The final 4Q expert must beat lag-1 on recent one-step RMSE and on both full and recent direct 1Q-4Q RMSE.",
      interval: `90% finite-sample OOS conformal after ${MIN_CONFORMAL_RESIDUALS} prior residuals; earlier points are explicitly labeled RMSE/component fallback.`,
      multiStep: "The selected expert is recursively projected for 4 quarters and is gated on direct 1Q-4Q rolling-origin errors; ridge revenue follows the median recent YoY rate. Interval radius expands by sqrt(horizon).",
      scenario: "capexIntensity multiplies future centers and interval radii only; monitoring and historical backtests are immutable.",
      vintagePolicy: "Targets and revenue features use SEC first-reported values only. Restated values and FMP are excluded from fitting and historical scoring.",
    },
    sources: [secSource],
  };
}
